import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguage } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";

const Header = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [phone, setPhone] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/site/contact").then((r) => setPhone(r.data.phone)).catch(() => {});
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const navItems = [
    { href: "/#rooms", key: "nav.rooms", testid: "nav-rooms" },
    { href: "/#gallery", key: "nav.gallery", testid: "nav-gallery" },
    { href: "/#safranbolu", key: "nav.attractions", testid: "nav-attractions" },
    { href: "/#contact", key: "nav.contact", testid: "nav-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled ? "bg-[#FAF6EC]/95 backdrop-blur-xl border-b border-[#CFC4A8]" : "bg-transparent"
      }`}
    >
      <div className="container-mag flex items-center justify-between h-16 lg:h-[88px] gap-6">
        <Link
          to="/"
          data-testid="site-logo"
          className={`font-display text-xl lg:text-[1.4rem] tracking-tight transition-colors whitespace-nowrap ${
            scrolled ? "text-[#0F0E0C]" : "text-[#FAF6EC]"
          }`}
        >
          Fatma Hanım Konağı
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <a
              key={item.key}
              href={item.href}
              data-testid={item.testid}
              className={`font-mono text-[10px] uppercase tracking-[0.32em] font-medium transition-colors whitespace-nowrap ${
                scrolled ? "text-[#0F0E0C] hover:text-[#1E3B2E]" : "text-[#FAF6EC]/90 hover:text-[#FAF6EC]"
              }`}
            >
              {t(item.key)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4 lg:gap-5">
          {phone && (
            <a
              href={`tel:${phone.replace(/\s/g, "")}`}
              data-testid="header-phone"
              className={`hidden xl:inline-flex items-center gap-2 text-xs font-medium whitespace-nowrap transition-colors ltr-numbers ${
                scrolled ? "text-[#0F0E0C] hover:text-[#1E3B2E]" : "text-[#FAF6EC]"
              }`}
            >
              <Phone size={13} />
              <span className="whitespace-nowrap">{phone}</span>
            </a>
          )}
          <LanguageSwitcher variant={scrolled ? "default" : "light"} />
          <Link
            to="/booking"
            data-testid="header-book-btn"
            className={`hidden md:inline-flex font-mono text-[10px] uppercase tracking-[0.32em] font-medium px-5 py-2.5 transition-colors whitespace-nowrap ${
              scrolled
                ? "bg-[#1E3B2E] text-[#FAF6EC] hover:bg-[#142A20]"
                : "border border-[#FAF6EC] text-[#FAF6EC] hover:bg-[#FAF6EC] hover:text-[#0F0E0C]"
            }`}
          >
            {t("nav.book")}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className={`lg:hidden p-2 ${scrolled ? "text-[#0F0E0C]" : "text-[#FAF6EC]"}`}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-[#FAF6EC] border-t border-[#CFC4A8] px-6 py-6 space-y-4">
          {navItems.map((item) => (
            <a
              key={item.key}
              href={item.href}
              className="block font-mono text-xs uppercase tracking-[0.32em] text-[#0F0E0C]"
              data-testid={`m-${item.testid}`}
            >
              {t(item.key)}
            </a>
          ))}
          <Link
            to="/booking"
            className="block text-center font-mono px-5 py-3 text-[10px] uppercase tracking-[0.32em] bg-[#1E3B2E] text-[#FAF6EC]"
            data-testid="m-book-btn"
          >
            {t("nav.book")}
          </Link>
          <div className="flex items-center justify-between pt-2">
            {phone && (
              <a
                href={`tel:${phone.replace(/\s/g, "")}`}
                className="text-[#0F0E0C] ltr-numbers text-sm"
                data-testid="m-phone"
              >
                <Phone size={14} className="inline me-2" /> {phone}
              </a>
            )}
            <LanguageSwitcher />
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
